import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UpsertAddress } from 'addresses/components';
import { IAddress, IContactGroupAddressAutocomplete } from 'addresses/types';
import {
  BillToLocationFormField,
  CustomerContactFormField,
  ShipToLocationFormField,
} from 'contacts/elements';
import { ContactGroupType } from 'contacts/enums';
import { IContact, IContactGroup, IContactGroupAddress } from 'contacts/types';
import { getLocationRequestPayloadByAlternateVersion } from 'contacts/utils';
import { FormField } from 'core/components';
import { Link } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IAutocompleteOption, IAutocompleteRef } from 'core/types';
import { useGetCustomerContactByEmail } from 'corporate/hooks';
import { ICorporateUserAutocomplete } from 'corporate/types';
import {
  IQuickQuoteContactPreload,
  TCustomerQuoteUpsertDisabledFields,
  TCustomerQuoteUpsertHiddenFields,
} from 'quotes/components/CustomerQuoteUpsert';
import { QuoteEntityType } from 'quotes/enums';
import { Level } from 'theme/enums';

import { getContactDefaultLocationsPrefill } from '../utils';

interface IProps {
  entityType: QuoteEntityType;
  projectId?: number;
  isAlternateVersionEnabled: boolean;
  hiddenFields?: TCustomerQuoteUpsertHiddenFields;
  disabledFields?: TCustomerQuoteUpsertDisabledFields;
  initialSelectedContactGroup?: IContactGroup | null;
  enableOneTimeShipToAddressEdit?: boolean;
  preloadContact?: IQuickQuoteContactPreload;
}

export const CustomerContactFormFields = ({
  entityType,
  projectId,
  isAlternateVersionEnabled,
  hiddenFields = {},
  disabledFields = {},
  initialSelectedContactGroup = null,
  enableOneTimeShipToAddressEdit = false,
  preloadContact,
}: IProps) => {
  const [selectedContactGroup, setSelectedContactGroup] = useState<IContactGroup | null>(
    initialSelectedContactGroup
  );
  const [isShipToAddressFormOpen, setIsShipToAddressFormOpen] = useState(false);

  const { t } = useTranslation();

  const { control, errors, setValue, watch } = useFormContext();

  const { record: preloadedContact } = useGetCustomerContactByEmail(
    preloadContact?.email,
    preloadContact?.contactGroupId
  );

  const billToRef = useRef<IAutocompleteRef>(null);
  const shipToRef = useRef<IAutocompleteRef>(null);
  const hasPreloadedContactRef = useRef(false);

  const projectValue = watch('project');
  const contactValue = watch('contact');
  const billToLocationValue = watch('billToLocation');
  const shipToLocationValue = watch('shipToLocation');

  const isOneTimeShipToAddress =
    enableOneTimeShipToAddressEdit && isOneTimeAddress(shipToLocationValue);

  const onChangeContact = (_: SyntheticEvent, contact: Partial<ICorporateUserAutocomplete>) => {
    setSelectedContactGroup(contact?.id ? contact?.contactGroup : null);

    // Unmounting a checkbox does not clear its form value, so a tick affirmed for the previous
    // contact would otherwise carry over and rewrite the new contact's default address.
    setValue('setBillToAsDefaultForContact', false);
    setValue('setShipToAsDefaultForContact', false);

    const { prefillBillTo, prefillShipTo } = getContactDefaultLocationsPrefill({
      billToAddress: contact?.billToAddress,
      shipToAddress: contact?.shipToAddress,
      isAlternateVersionEnabled,
      hasProjectSelected: !!(projectValue?.id || projectId),
      externalProjectAccounts: projectValue?.externalProjectAccounts,
    });

    if (!billToLocationValue && prefillBillTo) {
      onChangeBillToLocation(_, contact?.billToAddress || null, contact?.contactGroup);
    }

    if (!shipToLocationValue && prefillShipTo) {
      onChangeShipToLocation(_, contact?.shipToAddress || null, contact?.contactGroup);
    }
  };

  const onChangeBillToLocation = (
    _,
    location: IContactGroupAddress,
    contactGroup: Partial<IContactGroup> | null = null
  ) => {
    if (!location?.id) {
      setValue('billToLocation', null);
    } else if (
      location?.isBillTo &&
      (isAlternateVersionEnabled || location?.contactGroup?.id === contactGroup?.id) &&
      (entityType === QuoteEntityType.QUOTE ||
        !isAlternateVersionEnabled ||
        (entityType === QuoteEntityType.ORDER && !!location?.accountsReceivableId))
    ) {
      setValue('billToLocation', location);
    }

    if (contactGroup?.id) {
      return;
    }

    if (!location?.id && !shipToLocationValue && !contactValue) {
      setSelectedContactGroup(null);
    }

    if (!(contactValue?.id || shipToLocationValue?.id)) {
      setSelectedContactGroup(location?.contactGroup);

      return;
    }
  };

  const onChangeShipToLocation = (
    _,
    location: IContactGroupAddress,
    contactGroup: Partial<IContactGroup> | null = null
  ) => {
    if (!location?.id) {
      setValue('shipToLocation', null);
    } else if (
      location?.isShipTo &&
      (isAlternateVersionEnabled || location?.contactGroup?.id === contactGroup?.id)
    ) {
      setValue('shipToLocation', location);
    }

    if (contactGroup?.id) {
      return;
    }

    if (!location?.id && !billToLocationValue && !contactValue) {
      setSelectedContactGroup(null);
    }

    if (!(contactValue?.id || billToLocationValue?.id)) {
      setSelectedContactGroup(location?.contactGroup);

      return;
    }
  };

  const onCreateNewContact = (contact: IContact & { contactGroup: IContactGroup }) => {
    const newContact: Partial<ICorporateUserAutocomplete> = {
      id: contact.contactCorporateUserId,
      billToAddress: {
        ...contact.billingAddress,
        addressId: contact.billingAddress?.id,
        id: contact.billingCgAddressId,
      },
      companyId: contact.contactGroup.id,
      companyName: contact.contactGroup.name,
      contactGroup: contact.contactGroup,
      contactGroupId: contact.contactGroup.id,
      contactGroupDomains: contact.contactGroup.domains,
      contactId: contact.id,
      email: contact.email,
      externalUserId: contact.id,
      name: contact.name,
      phone: contact.phone,
      shipToAddress: {
        ...contact.shippingAddress,
        addressId: contact.shippingAddress?.id,
        id: contact.shippingCgAddressId,
      },
    };

    billToRef.current?.refetch();
    shipToRef.current?.refetch();

    onChangeContact(undefined, newContact);

    setValue('contact', newContact);
  };

  const onCreateNewLocation = (location: IAutocompleteOption) => {
    if (location?.id) {
      billToRef.current?.refetch();
      shipToRef.current?.refetch();
    }
  };

  // Add-in preload: reproduce a contact selection once the email lookup resolves.
  // Bill-to/ship-to are set through the same handlers a manual pick uses (which keep
  // the ORDER-requires-AR invariant), but bypassing onChangeContact's alternate AR
  // gate on purpose — that gate would otherwise skip a PROSPECT (no-AR) bill-to.
  useEffect(() => {
    if (hasPreloadedContactRef.current || !preloadedContact?.id) {
      return;
    }

    hasPreloadedContactRef.current = true;

    setSelectedContactGroup(preloadedContact.contactGroup ?? null);
    setValue('contact', preloadedContact);
    onChangeBillToLocation(
      undefined,
      preloadedContact.billToAddress,
      preloadedContact.contactGroup
    );
    onChangeShipToLocation(
      undefined,
      preloadedContact.shipToAddress,
      preloadedContact.contactGroup
    );
  }, [preloadedContact, setValue, onChangeBillToLocation, onChangeShipToLocation]);

  const onSubmitShipToAddress = (address: IAddress) => {
    setValue('shipToLocation', address, { shouldValidate: true });
  };

  const shipToAddressEditLink = isOneTimeShipToAddress ? (
    <Link
      level={Level.TITLE_SM}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        setIsShipToAddressFormOpen(true);
      }}
    >
      {t('buttonLabelEdit')}
    </Link>
  ) : null;

  // The contactGroup clause is load-bearing on BOTH gates and is not removable: it is the only
  // thing suppressing these checkboxes on surfaces that synthesize a contact without a
  // contactGroup (the quote Edit drawer, Create Customer Order) and whose mappers never send the
  // flags. hiddenFields does not participate in that suppression: those two surfaces pass a
  // hiddenFields that omits these keys.
  const displaySetBillToAsDefaultForContact =
    !hiddenFields.setBillToAsDefaultForContact &&
    !!contactValue?.contactGroup?.id &&
    !!billToLocationValue?.id &&
    billToLocationValue.id !== contactValue?.billToAddress?.id;

  const displaySetShipToAsDefaultForContact =
    !hiddenFields.setShipToAsDefaultForContact &&
    !!contactValue?.contactGroup?.id &&
    !!shipToLocationValue?.id &&
    shipToLocationValue.id !== contactValue?.shipToAddress?.id;

  const customerContactRequestPayload = useMemo(() => {
    return {
      viewBy: {
        ...(selectedContactGroup?.id && {
          contactGroupId: selectedContactGroup?.id,
          includeOtherContactGroups: 1,
        }),
      },
    };
  }, [selectedContactGroup]);

  const billToLocationRequestPayload = useMemo(() => {
    return getLocationRequestPayloadByAlternateVersion(
      {
        filters: {
          projectId: projectValue?.id || projectId,
          alternateVersionEnabled:
            isAlternateVersionEnabled && QuoteEntityType.ORDER === entityType ? 1 : 0,
          contactGroupType: ContactGroupType.CUSTOMER,
        },
      },
      selectedContactGroup?.id,
      isAlternateVersionEnabled
    );
  }, [projectValue, projectId, isAlternateVersionEnabled, entityType, selectedContactGroup?.id]);

  const shipToLocationRequestPayload = useMemo(() => {
    return getLocationRequestPayloadByAlternateVersion(
      {
        filters: {
          projectId: projectValue?.id || projectId,
          contactGroupType: ContactGroupType.CUSTOMER,
        },
      },
      selectedContactGroup?.id,
      isAlternateVersionEnabled
    );
  }, [projectValue, projectId, isAlternateVersionEnabled, selectedContactGroup?.id]);

  return (
    <>
      <CustomerContactFormField
        name="contact"
        label={t('labelCustomerQuoteCustomer')}
        contactGroup={selectedContactGroup}
        groupOptionsByContactGroup={
          !!selectedContactGroup?.id && (shipToLocationValue?.id || billToLocationValue?.id)
        }
        requestPayload={customerContactRequestPayload}
        hasCreateOption
        onCreateNewOptionSuccess={onCreateNewContact}
        onChange={onChangeContact as any}
        hidden={hiddenFields.contact}
        disabled={disabledFields.contact}
      />

      <BillToLocationFormField
        ref={billToRef}
        name="billToLocation"
        contactGroup={selectedContactGroup}
        groupOptionsByContactGroup={
          isAlternateVersionEnabled &&
          selectedContactGroup?.id &&
          (shipToLocationValue?.id || contactValue?.id)
        }
        requestPayload={billToLocationRequestPayload}
        onChange={onChangeBillToLocation as any}
        getLabel={getLocationLabel}
        getQueryFromValue={getLocationQueryFromValue}
        hasCreateOption={!!selectedContactGroup?.id && QuoteEntityType.ORDER !== entityType}
        onCreateNewOption={onCreateNewLocation}
        hidden={hiddenFields.billToLocation}
        disabled={disabledFields.billToLocation}
        disableClearable={false}
        level={1}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        disableArNumberFilterToggle={
          isAlternateVersionEnabled && entityType === QuoteEntityType.ORDER
        }
      />

      {displaySetBillToAsDefaultForContact && (
        <FormField
          name="setBillToAsDefaultForContact"
          type={InputType.CHECKBOX}
          inputProps={{
            control,
            error: errors.setBillToAsDefaultForContact,
            label: t('labelSetBillToAsDefaultForContact'),
          }}
        />
      )}

      <ShipToLocationFormField
        ref={shipToRef}
        name="shipToLocation"
        label={t('labelShipToLocation')}
        contactGroup={selectedContactGroup as any}
        groupOptionsByContactGroup={
          isAlternateVersionEnabled &&
          selectedContactGroup?.id &&
          (billToLocationValue?.id || contactValue?.id)
        }
        requestPayload={shipToLocationRequestPayload}
        onChange={onChangeShipToLocation as any}
        getLabel={getLocationLabel}
        getQueryFromValue={getLocationQueryFromValue}
        hasCreateOption={!!selectedContactGroup?.id}
        onCreateNewOption={onCreateNewLocation}
        hidden={hiddenFields.shipToLocation}
        disabled={disabledFields.shipToLocation}
        disableClearable={false}
        level={1}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        endDecorator={shipToAddressEditLink}
      />

      {displaySetShipToAsDefaultForContact && (
        <FormField
          name="setShipToAsDefaultForContact"
          type={InputType.CHECKBOX}
          inputProps={{
            control,
            error: errors.setShipToAsDefaultForContact,
            label: t('labelSetShipToAsDefaultForContact'),
          }}
        />
      )}

      {enableOneTimeShipToAddressEdit && (
        <UpsertAddress
          isOpen={isShipToAddressFormOpen}
          title={t('titleEditShipToAddress')}
          address={shipToLocationValue as Partial<IContactGroupAddressAutocomplete>}
          onClose={() => setIsShipToAddressFormOpen(false)}
          onSubmitSuccess={onSubmitShipToAddress}
          hasContactFields
          level={2}
        />
      )}
    </>
  );
};

// A one-time ship-to address carries address data but is not backed by a saved
// contact-group address — it has no AR number (the only signal the server sends to
// distinguish the two), so it can't be selected from the autocomplete and instead
// gets an inline "edit" affordance.
const isOneTimeAddress = (
  address?: Partial<IContactGroupAddressAutocomplete> & { contactGroup?: { id?: number } }
) => {
  if (!address?.name && !address?.id) {
    return false;
  }

  return (
    !address?.accountsReceivableId && !address?.contactGroupAddressId && !address?.contactGroup?.id
  );
};

const getLocationLabel = (option: IContactGroupAddress) => {
  if (!option?.id && !option?.name) {
    return '';
  }

  const label = [option.name];

  if (option.accountsReceivableId) {
    label.push(`[${option.accountsReceivableId}]`);
  }

  if (option.contactGroup?.name) {
    label.push(`(${option.contactGroup.name})`);
  }

  return label.join(' ');
};

const getLocationQueryFromValue = (label: string) => {
  if (!label) {
    return '';
  }

  let name = label;

  const bracketIndex = name.indexOf('[');
  if (bracketIndex !== -1) {
    name = name.substring(0, bracketIndex);
  }

  const parentIndex = name.indexOf('(');

  if (parentIndex !== -1) {
    name = name.substring(0, parentIndex);
  }

  return name.trim();
};
